import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, SyntheticBanner, SyntheticChip, Panel, PanelHeader } from "@/components/Common";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, LineChart, Line, Legend,
} from "recharts";
import {
  Activity, CheckCircle2, AlertTriangle, HelpCircle, ShieldCheck, MessagesSquare,
  UserCheck, TrendingUp, Rocket,
} from "lucide-react";

const READINESS_CFG = {
  READY: { bar: "bg-emerald-500", dot: "bg-emerald-500", text: "text-emerald-700" },
  "ACTION NEEDED": { bar: "bg-amber-500", dot: "bg-amber-500", text: "text-amber-700" },
  "REVIEW REQUIRED": { bar: "bg-rose-500", dot: "bg-rose-500", text: "text-rose-700" },
  "UNABLE TO EVALUATE": { bar: "bg-indigo-500", dot: "bg-indigo-500", text: "text-indigo-700" },
};

function LedgerBar({ dist, total }) {
  return (
    <div className="flex h-8 w-full overflow-hidden rounded-md border border-slate-200" data-testid="readiness-ledger-bar">
      {dist.map((s) => {
        const pct = total ? (s.value / total) * 100 : 0;
        const cfg = READINESS_CFG[s.name] || READINESS_CFG["REVIEW REQUIRED"];
        return (
          <div key={s.name} className={cn("flex h-full items-center justify-center", cfg.bar)}
            style={{ width: `${pct}%` }} title={`${s.name}: ${s.value}`}
            data-testid={`ledger-seg-${s.name.split(" ")[0].toLowerCase()}`}>
            {pct > 8 && <span className="font-display text-xs font-bold text-white">{s.value}</span>}
          </div>
        );
      })}
    </div>
  );
}

function MeterRow({ code, label, value, max, tone = "slate", testid }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-xs font-medium text-slate-700">
          {code && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-600">{code}</span>}
          <span className="truncate">{label}</span>
        </span>
        <span className="font-display text-sm font-bold text-slate-900">{value}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={cn("h-full rounded-full", tone === "amber" ? "bg-amber-500" : "bg-slate-700")} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function KpiCell({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-500", emerald: "text-emerald-600",
    amber: "text-amber-600", teal: "text-teal-600",
  };
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={testid}>
      <Icon className={`h-4 w-4 shrink-0 ${tones[tone]}`} />
      <div className="min-w-0">
        <div className="font-display text-xl font-bold leading-none text-slate-900">{value}</div>
        <div className="mt-1 truncate text-[11px] text-slate-500">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.dashboard().then(setD).catch(() => {}); }, []);
  if (!d) return <div className="text-sm text-slate-500">Loading dashboard…</div>;

  const totalDist = d.readiness_distribution.reduce((a, b) => a + b.value, 0);
  const maxUnresolved = Math.max(...d.top_unresolved.map((r) => r.count), 1);
  const maxRule = Math.max(...d.rule_trigger_frequency.map((r) => r.count), 1);

  return (
    <div>
      <PageHeader testid="dashboard-header" eyebrow="Operations Overview"
        title="Executive & Operations Dashboard"
        subtitle="Upstream reimbursement-readiness across evaluated laboratory orders. All figures are synthetic." />
      <SyntheticBanner />

      {/* What needs attention now — priority first */}
      <Panel className="mb-5 border-l-4 border-l-amber-500" data-testid="attention-band">
        <PanelHeader label="What needs attention now?" icon={AlertTriangle} />
        <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4">
          <button data-testid="attn-action" onClick={() => nav("/action-queue")}
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-left transition-colors hover:bg-amber-100">
            <div className="font-display text-2xl font-bold text-amber-800">{d.status_counts.ACTION_NEEDED}</div>
            <div className="text-xs text-amber-900">Orders needing action (Yellow)</div>
          </button>
          <button data-testid="attn-review" onClick={() => nav("/human-review")}
            className="rounded-md border border-slate-200 bg-slate-50 p-3 text-left transition-colors hover:bg-slate-100">
            <div className="font-display text-2xl font-bold text-slate-700">{d.human_reviews_pending}</div>
            <div className="text-xs text-slate-600">Human reviews pending</div>
          </button>
          <button data-testid="attn-clar" onClick={() => nav("/action-queue")}
            className="rounded-md border border-slate-200 bg-slate-50 p-3 text-left transition-colors hover:bg-slate-100">
            <div className="font-display text-2xl font-bold text-slate-700">{d.open_clarifications}</div>
            <div className="text-xs text-slate-600">Open clarifications</div>
          </button>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <div className="font-display text-2xl font-bold text-emerald-700">{d.issues_before_testing}</div>
            <div className="text-xs text-emerald-900">Issues caught before testing</div>
          </div>
        </div>
      </Panel>

      {/* Dense KPI ribbon (replaces uniform card grid) */}
      <Panel className="mb-5 overflow-hidden" data-testid="kpi-ribbon">
        <div className="grid grid-cols-2 divide-x divide-y divide-slate-200 sm:grid-cols-4 sm:divide-y-0 lg:grid-cols-8">
          <KpiCell icon={Activity} label="Orders Evaluated Today" value={d.orders_evaluated_today} tone="teal" testid="kpi-evaluated" />
          <KpiCell icon={CheckCircle2} label="READY" value={d.status_counts.READY} tone="emerald" testid="kpi-ready" />
          <KpiCell icon={AlertTriangle} label="ACTION NEEDED" value={d.status_counts.ACTION_NEEDED} tone="amber" testid="kpi-action" />
          <KpiCell icon={HelpCircle} label="REVIEW REQUIRED" value={d.status_counts.REVIEW_REQUIRED} tone="slate" testid="kpi-review" />
          <KpiCell icon={ShieldCheck} label="Issues Before Testing" value={d.issues_before_testing} tone="teal" testid="kpi-issues" />
          <KpiCell icon={MessagesSquare} label="Open Clarifications" value={d.open_clarifications} tone="amber" testid="kpi-clar" />
          <KpiCell icon={UserCheck} label="Human Reviews Pending" value={d.human_reviews_pending} tone="slate" testid="kpi-reviews" />
          <KpiCell icon={TrendingUp} label="Live Orders (session)" value={Object.values(d.live_status_counts).reduce((a, b) => a + b, 0)} tone="teal" testid="kpi-live" />
        </div>
      </Panel>

      {/* Operational split — evidence-oriented ledgers, not generic stock charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel data-testid="readiness-ledger">
          <PanelHeader label="Order Readiness Ledger" icon={CheckCircle2}
            right={<span className="text-[11px] text-slate-400">{totalDist} evaluated</span>} />
          <div className="p-4">
            <LedgerBar dist={d.readiness_distribution} total={totalDist} />
            {d.synthetic_note && <p className="mt-2 text-[10px] leading-snug text-slate-400" data-testid="dashboard-synthetic-note">{d.synthetic_note}</p>}
            <div className="mt-4 grid grid-cols-3 divide-x divide-slate-200 border-t border-slate-100 pt-3">
              {d.readiness_distribution.map((s) => {
                const cfg = READINESS_CFG[s.name] || READINESS_CFG["REVIEW REQUIRED"];
                const pct = totalDist ? Math.round((s.value / totalDist) * 100) : 0;
                return (
                  <div key={s.name} className="px-3 first:pl-0" data-testid={`readiness-stat-${s.name.split(" ")[0].toLowerCase()}`}>
                    <div className="flex items-center gap-1.5">
                      <span className={cn("h-2.5 w-2.5 rounded-full", cfg.dot)} />
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{s.name}</span>
                    </div>
                    <div className="mt-1 font-display text-xl font-bold text-slate-900">{s.value}</div>
                    <div className={cn("text-[11px] font-medium", cfg.text)}>{pct}% of evaluated</div>
                  </div>
                );
              })}
            </div>
          </div>
        </Panel>

        <Panel data-testid="daily-trend">
          <PanelHeader label="Daily Order Trend" icon={TrendingUp} />
          <div className="p-4">
            <ResponsiveContainer width="100%" height={196}>
              <LineChart data={d.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
                <Line type="monotone" dataKey="orders" stroke="#0f172a" strokeWidth={2} name="Orders" dot={false} />
                <Line type="monotone" dataKey="ready" stroke="#0d9488" strokeWidth={2} name="READY" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel data-testid="top-unresolved">
          <PanelHeader label="Top Unresolved Requirements" icon={AlertTriangle} />
          <div className="space-y-3 p-4">
            {d.top_unresolved.map((r) => {
              const [code, ...rest] = r.criterion.split(" — ");
              return (
                <MeterRow key={r.criterion} code={code} label={rest.join(" — ")} value={r.count}
                  max={maxUnresolved} tone="amber" testid={`unresolved-${code.toLowerCase()}`} />
              );
            })}
          </div>
        </Panel>

        <Panel data-testid="rule-frequency">
          <PanelHeader label="Policy / Rule Trigger Frequency" icon={ShieldCheck} />
          <div className="space-y-3 p-4">
            {d.rule_trigger_frequency.map((r) => (
              <MeterRow key={r.rule} code={r.rule.replace("Criterion ", "")} label={r.rule} value={r.count}
                max={maxRule} tone="slate" testid={`rule-${r.rule.replace(/\s+/g, "-").toLowerCase()}`} />
            ))}
          </div>
        </Panel>

        <Panel data-testid="clarification-turnaround">
          <PanelHeader label="Clarification Turnaround (hrs)" icon={MessagesSquare} />
          <div className="p-4">
            <ResponsiveContainer width="100%" height={188}>
              <LineChart data={d.clarification_turnaround}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} /><Tooltip />
                <Line type="monotone" dataKey="hours" stroke="#0d9488" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel className="p-4">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">Estimated Avoided Rework</div>
            <SyntheticChip />
          </div>
          <div className="font-display text-2xl font-bold text-slate-400">$ —</div>
          <div className="text-xs text-slate-500">Requires real-world validation.</div>
        </Panel>
        <Panel className="p-4">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">Estimated At-Risk Testing</div>
            <SyntheticChip />
          </div>
          <div className="font-display text-2xl font-bold text-slate-400">$ —</div>
          <div className="text-xs text-slate-500">Requires real-world validation.</div>
        </Panel>
        <Panel className="border-dashed border-teal-300 bg-teal-50/40 p-4" data-testid="future-capability-card">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-teal-800">
            <Rocket className="h-4 w-4" /> Future Capability
          </div>
          <div className="text-sm font-semibold text-slate-800">Laboratory Commercial & Reimbursement Intelligence</div>
          <p className="mt-1 text-xs text-slate-600">
            Potential future capability to help laboratories evaluate reimbursement and operational
            requirements when launching or expanding diagnostic tests — payer requirement landscape,
            documentation burden, test-launch readiness, provider readiness, policy-change impact.
          </p>
          <div className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-teal-700">Not implemented — roadmap only</div>
        </Panel>
      </div>
    </div>
  );
}
